import React, { useEffect, useState } from 'react'
import {
  getAllQueryParamsAsObjectFromUrl,
  replaceUrl
} from '../../../../../../../../utils/UrlUtils'
import {
  getStripeInvoices,
  getStripePlanDetails
} from '../../../../../../../../api/stripe/stripe.service'

import BillingInvoices from '../Screen/BillingInvoices'
import BillingOverview from '../Screen/BillingOverview'
import BillingPlans from '../Screen/BillingPlans'
import ContextSwitcherTabs from '../../../../../../../../components/common/DDS/ContextSwitcherTab/ContextSwitcherTab'
import { OrganizationUserRoles } from '../../../../../../../../enum/OrganizationUserRoles.enum'
import { ToastMessageConstants } from '../../../../../../../../constants/ToastMessageConstants'
import ToastNotification from '../../../../../../../../components/common/DDS/Toast/Toast'
import { getUserDetails } from '../../../../../../../../api/user/User.service'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../../../../../global/newPallete'
import routeConstants from '../../../../../../../../constants/RouteConstants'
import { useSearchParams } from 'react-router-dom'
import { useStoreContext } from '../../../../../../../../store/StoreContext'

type SubscriptionDetails = {
  id: string
  plan: any
  planId: string
  status: any
  jobMonitoring: number
  apiMonitoring: number
  applicationHealthCheck: number
  errorManagement: number
  project: number
  environment: number
  user: number
  dataRetention: number
  nextBillingAt: Date
  additionalBenefits?: any
  billingPeriodUnit: string
  billingPeriod: number
  subscriptionAmount: string
  stripePriceId: any
  stripeProductId: any
}

interface BillingCardOrganizationProp {
  usageDataLastSync: any
}

const BillingOrganizationSetting: React.FC<BillingCardOrganizationProp> = ({
  usageDataLastSync
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tabIndex, setTabIndex] = useState<string>('1')

  const store = useStoreContext()
  const [stripePlanData, setStripePlanData] = useState<any>()

  const [userLoading, setUserLoading] = useState<boolean>(false)
  const [isPlanDetailsLoading, setIsPlanDetailsLoading] =
    useState<boolean>(false)

  const [subscriptionDetails, setSubscriptionDetails] =
    useState<SubscriptionDetails>()
  const [organizationDetail, setOrganizationDetail] = useState<any>()

  const formatUsageDataLastSync = (isoDateString: string): string => {
    const date = new Date(isoDateString)

    // Format month, day, and year
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const year = date.getFullYear()

    // Format hours and minutes
    let hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'

    // Convert to 12-hour format
    hours = hours % 12
    hours = hours ? hours : 12 // handle midnight (0 hours)

    return `${month}/${day}/${year}, ${hours}:${minutes} ${ampm}`
  }

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed '
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              Manage Organization
            </div>
          </div>
        ),
        link:
          replaceUrl(
            routeConstants.MANAGE_ORGANIZATION,
            store.scopeStore.getScope()['organization-id'] as string
          ) + '?tab=3'
      },
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed '
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              Billing
            </div>
          </div>
        ),
        link:
          replaceUrl(
            routeConstants.MANAGE_ORGANIZATION,
            store.scopeStore.getScope()['organization-id'] as string
          ) + '?tab=3'
      },
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed '
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              {tabIndex === '1'
                ? 'Plan Details'
                : tabIndex === '2'
                  ? 'Usage History'
                  : 'Invoice'}
            </div>
          </div>
        ),
        link:
          replaceUrl(
            routeConstants.MANAGE_ORGANIZATION,
            store.scopeStore.getScope()['organization-id'] as string
          ) + '?tab=3'
      }
    ])
  }, [store.breadcrumbStore, tabIndex, store.scopeStore])

  useEffect(() => {
    setUserLoading(true)
    store.uiStore.setGlobalLoader(true)

    getUserDetails()
      .then((userResponse: any) => {
        const organizationDetails = userResponse.data.organizations.find(
          (org: any) =>
            org.id === store.userStore.getSelectedOrganization().id &&
            org.membership.role !== OrganizationUserRoles.MEMBER
        )

        store.userStore.setOrganizations([...userResponse.data.organizations])
        setOrganizationDetail(organizationDetails)

        const subscriptionDetails = userResponse.data.organizations.find(
          (org: any) => org.id === store.scopeStore.getOrganizationId()
        )?.subscription

        setSubscriptionDetails(subscriptionDetails)
      })
      .catch((e) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.STRIPE
        })
      })
      .finally(() => {
        setUserLoading(false)
        store.uiStore.setGlobalLoader(false)
      })
  }, [])

  const fetchPlanDetails = () => {
    setIsPlanDetailsLoading(true)

    getStripePlanDetails({
      $stripePriceId:
        organizationDetail?.subscription?.stripePriceId ||
        subscriptionDetails?.stripePriceId,
      $stripeProductId: organizationDetail?.subscription?.stripeProductId,
      $subscriptionId: organizationDetail?.subscription?.stripeSubscriptionId
    })
      .then((response: any) => {
        setStripePlanData(response.data)
        store.userStore.setPlanType(response.data.planType)
      })
      .catch((e: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.STRIPE
        })
      })
      .finally(() => {
        setIsPlanDetailsLoading(false)
      })
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const sessionId = params.get('session_id')
    const status = organizationDetail?.subscription?.status

    if (sessionId) {
      switch (status) {
        case 'active':
          ToastNotification({
            type: 'success',
            message:
              '🎉 Payment successful! Your subscription is active. You can now access all premium features.'
          })
          break

        case 'trialing':
          ToastNotification({
            type: 'success',
            message:
              '🚀 Welcome to your trial! Explore all features during your trial period. Upgrade anytime to continue without interruption.'
          })
          break

        case 'incomplete':
          ToastNotification({
            type: 'info',
            message:
              '⚠️ Payment was not completed. Please complete your subscription to unlock all features.'
          })
          break

        case 'incomplete_expired':
          ToastNotification({
            type: 'error',
            message:
              '❌ Your previous payment session expired. Start a new subscription to continue using our services.'
          })
          break

        case 'past_due':
          ToastNotification({
            type: 'info',
            message:
              '⚠️ Your subscription payment is past due. Update your payment method to avoid service interruption.'
          })
          break

        case 'canceled':
          ToastNotification({
            type: 'info',
            message:
              'ℹ️ Your subscription has been canceled. Reactivate anytime to resume access to premium features.'
          })
          break
      }
    }
  }, [location, organizationDetail])

  useEffect(() => {
    if (
      organizationDetail?.subscription?.stripePriceId &&
      organizationDetail?.subscription?.stripeProductId &&
      organizationDetail?.stripeCustomerId
    ) {
      fetchPlanDetails()
    }
  }, [organizationDetail])

  const ContextSwitcherTab = [
    {
      id: '1',
      label: 'Plan Details',
      children: (
        <div>
          <BillingOverview
            isPlanDetailsLoading={isPlanDetailsLoading}
            stripePlanData={stripePlanData}
            userLoading={userLoading}
            organizationDetail={
              organizationDetail || store.userStore.getOrganizationDetails()
            }
            subscriptionDetails={subscriptionDetails}
          />
        </div>
      )
    },
    {
      id: '2',
      label: 'Usage History',
      children: (
        <div>
          <BillingPlans />
        </div>
      )
    },
    {
      id: '3',
      label: 'Invoices',
      children: (
        <div>
          <BillingInvoices />
        </div>
      )
    }
  ]

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const tabFromUrl = queryParams.get('contextSwitcherTab')
    if (tabFromUrl) {
      setTabIndex(tabFromUrl)
    }
  }, [])

  return (
    <div className='py-[24px] relative'>
      <div className='flex items-start w-full'>
        <div className='flex-grow relative'>
          <ContextSwitcherTabs
            tabOptions={ContextSwitcherTab}
            shape='roundedRectangle'
            tabStyle={{
              border: '1px solid',
              borderColor: pallete.colors.stroke2,
              userSelect: 'none',
              width: '400px'
            }}
            tabTitleClassName='text-center select-none px-[16px]'
            activeTab={tabIndex}
            onChange={(index: any) => {
              setTabIndex(index.toString())
              const queryParams = getAllQueryParamsAsObjectFromUrl(
                location.search
              )
              setSearchParams({
                ...queryParams,
                page: '1',
                contextSwitcherTab: index.toString()
              })
            }}
            className='override-view-more-tabs override-height'
          />

          {!isPlanDetailsLoading && !userLoading ? (
            <div
              className='absolute top-0 right-0'
              style={{
                fontSize: '12px',
                lineHeight: '16px',
                fontWeight: 400,
                color: pallete.colors.textDark5
              }}
            >
              {usageDataLastSync
                ? `Last sync on ${formatUsageDataLastSync(usageDataLastSync)}`
                : 'Sync data unavailable'}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default observer(BillingOrganizationSetting)
